import { DialogActions, Paper, Popper, TextField } from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import {
  calculateDays,
  getRealDaysToCount,
  isDateAWeekDay,
} from 'lib/functions/dates';
import { formatDate, showAlert } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

import { DocumentWithDates, PopperUniqueDateProps } from '../../types';

const PopperUniqueDate = (props: PopperUniqueDateProps) => {
  const {
    selectedDocuments,
    setSelectedDocuments,
    open,
    setOpen,
    anchorEl,
    placement,
    canForcePaymentDate,
    minimumDays,
    maximumDays,
    operationDate,
  } = props;
  const dispatch = useDispatch();
  const [uniqueDate, setUniqueDate] = React.useState<string | null>(null);

  const dateValidators = (date: Date) => {
    if (canForcePaymentDate) return true;

    if (!isDateAWeekDay(date)) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento debe ser un día hábil',
      );
      return false;
    }

    const minimumDate = new Date(operationDate);
    minimumDate.setDate(minimumDate.getDate() + minimumDays);
    if (date.getTime() <= minimumDate.getTime()) {
      showAlert(
        dispatch,
        'warning',
        `La fecha de vencimiento no puede ser menor o igual a ${minimumDays} días a partir de la fecha de operación`,
      );
      return false;
    }

    if (maximumDays && maximumDays > 0) {
      for (const document of selectedDocuments) {
        const globalMaximumDate = new Date(operationDate);
        globalMaximumDate.setDate(
          globalMaximumDate.getDate() + maximumDays - 1,
        );
        const issuedDateLimit = new Date(document.issuedDate);
        issuedDateLimit.setDate(issuedDateLimit.getDate() + maximumDays - 1);
        if (date.getTime() > issuedDateLimit.getTime()) {
          showAlert(
            dispatch,
            'warning',
            `La fecha de vencimiento no puede ser mayor o igual a ${maximumDays} días que la fecha de emisión.`,
          );
          return false;
        }
        if (date.getTime() > globalMaximumDate.getTime()) {
          showAlert(
            dispatch,
            'warning',
            `La fecha de vencimiento no puede ser mayor o igual a ${maximumDays} días a partir de la fecha de operación`,
          );
          return false;
        }
      }
    }

    for (const requestInvoice of selectedDocuments) {
      const days = calculateDays(
        new Date(requestInvoice.issuedDate),
        new Date(date),
      );
      if (days <= 0) {
        showAlert(
          dispatch,
          'warning',
          'La fecha de vencimiento no puede ser menor a la fecha de emisión',
        );
        return false;
      }
    }
    return true;
  };

  const handleChangeDateNormal = () => {
    if (!uniqueDate) return;
    const date = new Date(uniqueDate);
    const valid = dateValidators(date);
    if (!valid) return;

    const newSelectedDocuments = selectedDocuments.map(
      (doc: DocumentWithDates) => {
        const subDocuments = doc.subDocuments?.map(
          (subDoc: Record<string, unknown>) => {
            return {
              ...subDoc,
              dueDate: date?.toISOString(),
              paymentDate: date,
              tempDueDate: formatDate(date),
              daysToCount: getRealDaysToCount(date, operationDate),
            };
          },
        );
        return {
          ...doc,
          dueDate: date?.toISOString(),
          paymentDate: date,
          tempDueDate: formatDate(date),
          daysToCount:
            subDocuments && subDocuments.length === 0
              ? getRealDaysToCount(date, operationDate)
              : null,
          subDocuments,
        };
      },
    );

    setSelectedDocuments(newSelectedDocuments);
  };

  const handleApplyUniqueDate = () => {
    handleChangeDateNormal();
    setOpen(false);
  };

  return (
    <Popper
      sx={{ zIndex: 1300 }}
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition={false}
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            altBoundary: true,
            tether: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
      ]}
    >
      <Paper style={{ padding: 8 }}>
        <TextField
          value={uniqueDate}
          type="date"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setUniqueDate(e.target.value)
          }
        />
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            Salir
          </CustomButton>
          <CustomButton onClick={() => handleApplyUniqueDate()}>
            Aplicar
          </CustomButton>
        </DialogActions>
      </Paper>
    </Popper>
  );
};

export default PopperUniqueDate;
